import React, { useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import LandingPage from '../../views/PublicViews/LandingPage';
import SignupPage from '../../views/PublicViews/SignupPage';
import LoginPage from '../../views/PublicViews/LoginPage';
import Home from '../../views/ProtectedViews/Home/Home';
import { InitializationRoute } from './InitializationRoute';
import { CompleteStripeOnboardingRoute } from './CompleteStripeOnboardingRoute';
import { useAuthContext } from '../../context/auth/AuthContext';
import SignupCompletionPage from '../../views/PublicViews/SignupCompletionPage';

// Import all protected view components
import Profile from '../../views/ProtectedViews/Profile/Profile';
import Organization from '../../views/ProtectedViews/Organization/Organization';
import Subscription from '../../views/ProtectedViews/Subscription/Subscription';
import BiziBoard from '../../views/ProtectedViews/BiziBoard/BiziBoard';
import ThreadView from '../../views/ProtectedViews/BiziBoard/ThreadView';
import BiziDocs from '../../views/ProtectedViews/BiziDocs/BiziDocs';
import Invoices from '../../views/ProtectedViews/Invoices/Invoices';
import InvoiceDetail from '../../views/ProtectedViews/Invoices/InvoiceDetail';
import InvoiceCreationForm from '../../views/ProtectedViews/Invoices/InvoiceCreationForm';
import AssetManagement from '../../views/ProtectedViews/Assets/Assets';
import AssetDetail from '../../views/ProtectedViews/Assets/AssetDetail';
import Customers from '../../views/ProtectedViews/Customers/Customers';
import CustomerDetail from '../../views/ProtectedViews/Customers/CustomerDetail';
import Departments from '../../views/ProtectedViews/Employees/Departments/Departments';
import DepartmentDetail from '../../views/ProtectedViews/Employees/Departments/DepartmentDetail';
import Employees from '../../views/ProtectedViews/Employees/Employees/Employees';
import EmployeeDetail from '../../views/ProtectedViews/Employees/Employees/EmployeeDetail';
import Inventory from '../../views/ProtectedViews/Inventory/Inventory';
import InventoryItemDetail from '../../views/ProtectedViews/Inventory/InventoryItemDetail';
import Products from '../../views/ProtectedViews/Products/Products';
import ProductDetail from '../../views/ProtectedViews/Products/ProductDetail';
import Settings from '../../views/ProtectedViews/Settings/Settings';
import Suppliers from '../../views/ProtectedViews/Suppliers/Suppliers';
import SupplierDetail from '../../views/ProtectedViews/Suppliers/SupplierDetail';
import UnitOfMeasure from '../../views/ProtectedViews/UnitOfMeasure/UnitOfMeasure';
import UnitOfMeasureDetail from '../../views/ProtectedViews/UnitOfMeasure/UnitOfMeasureDetail';
import Locations from '../../views/ProtectedViews/Locations/Locations';
import LocationDetail from '../../views/ProtectedViews/Locations/LocationDetail';
import Roles from '../../views/ProtectedViews/Roles/Roles';
import RoleDetail from '../../views/ProtectedViews/Roles/RoleDetails';
import Approvals from '../../views/ProtectedViews/Scheduling/Approvals/Approvals';
import JobCodes from '../../views/ProtectedViews/Scheduling/JobCodes/JobCodes';
import ReviewTimeClocks from '../../views/ProtectedViews/Scheduling/TimeClock/ReviewTimeClocks';
import Calendar from '../../views/ProtectedViews/Calendar/Calendar';
import Timeclock from '../../views/ProtectedViews/Scheduling/TimeClock/TimeClock';
import TimeSheet from '../../views/ProtectedViews/Scheduling/TimeSheets/TimeSheet';
import PerformanceReviews from '../../views/ProtectedViews/PerformanceReviews/PerformanceReviews';
import PerformanceReviewTemplates from '../../views/ProtectedViews/PerformanceReviews/PerformanceReviewTemplates/PerformanceReviewTemplates';
import PerformanceReviewDetail from '../../views/ProtectedViews/PerformanceReviews/PerformanceReviewDetail';
import InventoryOrders from '../../views/ProtectedViews/InventoryOrders/InventoryOrders';
import InventoryOrderDetail from '../../views/ProtectedViews/InventoryOrders/InventoryOrderDetail';
import PointOfSale from '../../views/ProtectedViews/PointOfSale/PointOfSale';

// Import public view components
import About from '../../components/footer/About';
import PrivacyPolicy from '../../components/footer/PrivacyPolicy';
import TermsOfService from '../../components/footer/TermsOfService';
import CookiePolicy from '../../components/footer/CookiePolicy';
import PrivacyChoices from '../../components/footer/PrivacyChoices';
import ProhibitedBusinesses from '../../components/footer/ProhibitedBusinesses';
import Success from '../../views/PublicViews/Success';
import PerformanceReviewTemplateDetail from '../../views/ProtectedViews/PerformanceReviews/PerformanceReviewTemplates/PerformanceReviewTemplateDetail';
import { Loading } from '../loading/Loading';
import { UserStatus } from './routing_statuses/RoutingStatuses';
import LoadingNavBar from '../navbar/LoadingNavBar';
import Processing from '../../views/PublicViews/SignupFlow/Processing';
import { ProtectedRoute } from './ProtectedRoute';
import InvoiceSettings from '../../views/ProtectedViews/Invoices/settings/InvoiceSettings';
import { useUserContext } from '../../context/user_context/UserContext';
import { useOrganizationContext } from '../../context/organization_context/OrganizationContext';
import { useSubscriptionContext } from '../../context/subscription_context/SubscriptionContext';
import InactiveSubscription from '../../views/ProtectedViews/InactiveSubscription/InactiveSubscription';
import BaseNavBar from '../navbar/BaseNavBar';
import Dashboard from '../../views/ProtectedViews/Invoices/dashboard/Dashboard';

export default function AppRouter() {
  const { user } = useAuthContext();
  const { firebaseUser, userRole } = useUserContext();
  const { 
    organization, 
    organizationStripeInfo, 
    organizationRoles,
    subscriptionInitialized 
  } = useOrganizationContext();
  const { subscription, subscriptionStatus, hasActiveSubscription } = useSubscriptionContext();
  const location = useLocation();
  const routeEvaluated = useRef(false);
  const [isUserInitialized, setIsUserInitialized] = useState(false);
  const [routeStabilized, setRouteStabilized] = useState(false);
  const isLoading = user === UserStatus.UNINITIALIZED;
  
  const isSubscriptionDataLoaded = subscription && 
                                  subscription !== UserStatus.UNINITIALIZED && 
                                  subscriptionStatus !== null;
  
  const isSubscriptionCompleted = subscriptionInitialized || organization?.subscription_initialized === true;
  
  useEffect(() => {
    const routerLoadTime = parseInt(sessionStorage.getItem('routerLoadTime') || '0');
    const now = Date.now();
    
    if (!routerLoadTime || (now - routerLoadTime > 30000)) {
      sessionStorage.setItem('routerLoadTime', now.toString());
      sessionStorage.setItem('stabilizedRoute', location.pathname);
    }
    
    const stabilizeTimeout = setTimeout(() => {
      setRouteStabilized(true);
    }, 3000);
    
    return () => clearTimeout(stabilizeTimeout);
  }, []);
  
  // Get the route we've stabilized on
  const stabilizedRoute = sessionStorage.getItem('stabilizedRoute') || '/';
  
  // Log important state changes only
  useEffect(() => {
    if (user !== UserStatus.UNINITIALIZED && !isUserInitialized) setIsUserInitialized(true);
    
  }, [user, firebaseUser, organization, subscription, hasActiveSubscription, subscriptionStatus, isUserInitialized, stabilizedRoute]);

  const initialRoute = React.useMemo(() => {
    if (routeEvaluated.current) return "/"; 
    if (user === UserStatus.UNINITIALIZED) return "/";
    routeEvaluated.current = true;
    if (organization && organization.processing) return '/processing';

    if (user && user !== UserStatus.UNAUTHENTICATED) {
      
      const hasIncompleteProfile = 
        (firebaseUser === UserStatus.DOES_NOT_EXIST) ||
        (firebaseUser && !firebaseUser.first_name) ||
        (organization === UserStatus.DOES_NOT_EXIST) || 
        (organization && !organization.name) ||
        (subscription === UserStatus.DOES_NOT_EXIST) ||
        (subscription && !subscription.customer);
      
        const isStillInitializing = 
        firebaseUser === UserStatus.UNINITIALIZED || 
        organization === UserStatus.UNINITIALIZED ||
        subscription === UserStatus.UNINITIALIZED;
      
      if (firebaseUser === UserStatus.DOES_NOT_EXIST) return '/signup-completion';
      if (isStillInitializing) return '/home';
      if (hasIncompleteProfile) return '/signup-completion'
      
      const hasCompletedBasicProfile = firebaseUser && firebaseUser.first_name && 
                                     organization && organization.name;
      const needsSubscriptionSetup = hasCompletedBasicProfile && 
                                    !isSubscriptionCompleted && 
                                    (!hasActiveSubscription || 
                                     !subscription || 
                                     subscription === UserStatus.DOES_NOT_EXIST || 
                                     !subscription.customer);
                                     
      if (needsSubscriptionSetup) return '/signup-completion'
      if (organizationStripeInfo && 
          !organizationStripeInfo.onboarding_complete && 
          subscription && 
          (subscription.status === 'active' || subscription.status === 'trialing')) {
        return '/home'
      }
      return '/home'
    }

    // Default to landing page for unauthenticated users
    console.log("[AppRouter] Initial route: / (unauthenticated user)");
    return '/';
  }, [user, organization, subscription, firebaseUser, organizationStripeInfo]);
  
  // Show loading state while initializing
  if (isLoading) { 
    return (
      <>
        <LoadingNavBar />
        <Loading />
      </>
    );
  }

  const needsStripeOnboarding = (path) => {
    if (!isSubscriptionDataLoaded) return false;
    if (path === '/subscription') return false;
    const needsOnboarding = hasActiveSubscription && 
                          organizationStripeInfo && 
                          !organizationStripeInfo.onboarding_complete;
    return needsOnboarding;
  };
  
  const hasRequiredRoles = () => {
    return userRole && organizationRoles;
  };

  const renderProtectedComponent = (path, component) => {
    if (!routeStabilized) {
      return (
        <>
          <BaseNavBar />
          <Loading />
        </>
      );
    }
        
    const isAlreadyOnSignupCompletion = location.pathname === '/signup-completion';
    
    if (isSubscriptionCompleted && !isAlreadyOnSignupCompletion) {
      if (firebaseUser === UserStatus.DOES_NOT_EXIST || 
          (firebaseUser && !firebaseUser.first_name) ||
          organization === UserStatus.DOES_NOT_EXIST || 
          (organization && !organization.name)) {
        return <Navigate to="/signup-completion" replace />;
      }
    }
    else if (!isSubscriptionCompleted && !isAlreadyOnSignupCompletion) {
      return <Navigate to="/signup-completion" replace />;
    }
    
    if (!hasActiveSubscription && isSubscriptionCompleted && !isAlreadyOnSignupCompletion) {
      if (path === '/home' && subscription) return <Navigate to="/subscription" replace />;
      return <InactiveSubscription />;
    }
    
    if (needsStripeOnboarding(path)) return <CompleteStripeOnboardingRoute />;
    if (!hasRequiredRoles()) return <><BaseNavBar /><Loading /></>;
    
    return component;
  };

  const isFullyAuthenticated = user && user !== UserStatus.UNAUTHENTICATED && 
                              organization && subscription && firebaseUser;

  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/" 
        element={
          isFullyAuthenticated ? 
            <Navigate to="/home" replace /> : 
            user && user !== UserStatus.UNAUTHENTICATED ? 
              <Loading /> :
              <LandingPage />
        } 
      />
      
      <Route 
        path="/login" 
        element={
          user === UserStatus.UNAUTHENTICATED ? 
            <LoginPage /> : 
            isFullyAuthenticated ? 
              <Navigate to="/home" replace /> :
              <Loading />
        } 
      />
      
      <Route 
        path="/signup" 
        element={
          user === UserStatus.UNAUTHENTICATED ? (
            <SignupPage /> 
          ) : user === UserStatus.UNINITIALIZED ? (
            <Loading />
          ) : (
            <Navigate to={initialRoute} replace />
          )
        } 
      />
      
      {/* Static Public Routes - always accessible */}
      <Route path="/about" element={<About />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/privacy-choices" element={<PrivacyChoices />} />
      <Route path="/prohibited-businesses" element={<ProhibitedBusinesses />} />
      <Route path="/success" element={<Success />} />

      {/* Signup Completion Route */}
      <Route
        path="/signup-completion"
        element={
          !routeStabilized ? (
            <>
              <LoadingNavBar />
              <Loading />
            </>
          ) : (
            (() => {
              if (!user || user === UserStatus.UNAUTHENTICATED || user === UserStatus.UNINITIALIZED) return <Navigate to="/" />
              if (firebaseUser && 
                  firebaseUser !== UserStatus.DOES_NOT_EXIST && 
                  firebaseUser.first_name && 
                  organization && 
                  organization !== UserStatus.DOES_NOT_EXIST && 
                  organization.name && 
                  isSubscriptionCompleted) {
                    
                return <Navigate to="/home" replace />;
              }
              return <SignupCompletionPage />;
            })()
          )
        }
      />
      
      {/* Processing Route */}
      <Route path="/processing" element={<Processing />} />

      {/* Protected Routes */}
      {/* General Protected Routes */}
      <Route path="/home" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/home', <Home />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/profile" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/profile', <Profile />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/organization" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/organization', <Organization />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/subscription" element={
        <InitializationRoute>
          <ProtectedRoute>
            <Subscription />
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/settings" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/settings', <Settings />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />

      {/* BiziBoard Routes */}
      <Route path="/biziboard/:threadId" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/biziboard', <ThreadView />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/biziboard" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/biziboard', <BiziBoard />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />

      {/* BiziDocs Routes */}
      <Route path="/bizidocs" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/bizidocs', <BiziDocs />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />

      {/* Invoice Routes */}
      <Route path="/invoices" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/invoices', <Invoices />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/invoices/:invoiceId" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/invoices', <InvoiceDetail />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />

      <Route path="/invoice-dashboard" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/invoice-dashboard', <Dashboard />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />

      <Route path="/calendar" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/calendar', <Calendar />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />

      <Route path="/review-time-clocks" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/review-time-clocks', <ReviewTimeClocks/>)}
          </ProtectedRoute>
        </InitializationRoute>
      } />

      <Route path="/time-clock" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/time-clock', <Timeclock/>)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/invoice-creation-form" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/invoice-creation-form', <InvoiceCreationForm />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/invoice-settings" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/invoice-settings', <InvoiceSettings />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />

      {/* Asset Routes */}
      <Route path="/assets" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/assets', <AssetManagement />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/assets/:assetId" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/assets', <AssetDetail />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />

      {/* Customer Routes */}
      <Route path="/customers" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/customers', <Customers />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/customers/:customerId" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/customers', <CustomerDetail />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />

      {/* Employee Routes */}
      <Route path="/departments" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/departments', <Departments />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/departments/:departmentId" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/departments', <DepartmentDetail />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/employees" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/employees', <Employees />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/employees/:employeeId" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/employees', <EmployeeDetail />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      {/* Performance Review Routes */}
      <Route path="/performance-reviews/:reviewId" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/performance-reviews', <PerformanceReviewDetail />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/performance-reviews" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/performance-reviews', <PerformanceReviews />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/performance-review-templates" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/performance-review-templates', <PerformanceReviewTemplates />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/performance-review-templates/:templateId" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/performance-review-templates', <PerformanceReviewTemplateDetail />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />

      {/* Inventory Routes */}
      <Route path="/inventory" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/inventory', <Inventory />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/inventory/:itemId" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/inventory', <InventoryItemDetail />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/inventory-orders" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/inventory-orders', <InventoryOrders />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/inventory-orders/:orderId" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/inventory-orders', <InventoryOrderDetail />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/products" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/products', <Products />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/products/:productId" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/products', <ProductDetail />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/suppliers" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/suppliers', <Suppliers />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/suppliers/:supplierId" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/suppliers', <SupplierDetail />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/unit-of-measure" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/unit-of-measure', <UnitOfMeasure />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/unit-of-measure/:uomId" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/unit-of-measure', <UnitOfMeasureDetail />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/locations" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/locations', <Locations />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/locations/:locationId" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/locations', <LocationDetail />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />

      {/* Role Routes */}
      <Route path="/roles" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/roles', <Roles />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      <Route path="/roles/:roleId" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/roles', <RoleDetail />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />

      {/* Scheduling Routes */}
      <Route path="/approvals" element={
        <InitializationRoute>
          <ProtectedRoute>
            {renderProtectedComponent('/approvals', <Approvals />)}
          </ProtectedRoute>
        </InitializationRoute>
      } />
      
      {/* Signup Completion Route */}
      <Route
        path="/processing"
        element={<Processing />}
      />

      {/* Catch-all redirect */}
      <Route 
        path="*" 
        element={
          isFullyAuthenticated ? 
            <Navigate to="/home" replace /> : 
            <Navigate to={initialRoute} replace />
        } 
      />
    </Routes>
  );
} 